import React, { useState, useRef } from 'react';
import { X, UserPlus, Shield, Sparkles, Check, Hash, Activity, Camera, Upload } from 'lucide-react';
import { FootballPosition, PreferredFoot, GoalkeeperAbility, GroupMemberProfile, UserAccount } from '../types';
import { NumberInput } from './NumberInput';
import { compressImage } from '../utils/imageCompressor';

interface RegisterPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  defaultJoinCode: string;
  existingMemberCount: number;
  onRegisterMember: (newMember: GroupMemberProfile, newUser: UserAccount, password: string) => Promise<string | null>;
}

export const RegisterPlayerModal: React.FC<RegisterPlayerModalProps> = ({
  isOpen,
  onClose,
  groupId,
  defaultJoinCode,
  existingMemberCount,
  onRegisterMember,
}) => {
  if (!isOpen) return null;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fullName, setFullName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80');
  const [joinCode, setJoinCode] = useState(defaultJoinCode);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Image must be under 10MB');
        return;
      }
      try {
        const compressed = await compressImage(file, 250, 250, 0.75);
        setAvatarUrl(compressed);
      } catch (err) {
        console.error('Failed to compress image:', err);
      }
    }
  };
  const [jerseyNumber, setJerseyNumber] = useState(existingMemberCount + 10);
  const [primaryPosition, setPrimaryPosition] = useState<FootballPosition>('CM');
  const [preferredFoot, setPreferredFoot] = useState<PreferredFoot>('Right');
  const [goalkeeperAbility, setGoalkeeperAbility] = useState<GoalkeeperAbility>('Sometimes');
  const [favoriteClub, setFavoriteClub] = useState('Real Madrid');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || !fullName.trim()) {
      setErrorMsg('Please enter your full name and display name.');
      return;
    }

    const normalizedPhoneNumber = phoneNumber.trim();
    const normalizedWhatsappNumber = whatsappNumber.trim();

    if (!normalizedPhoneNumber) {
      setErrorMsg('Active phone number is required so an organizer can contact the player.');
      return;
    }

    const phoneDigits = normalizedPhoneNumber.replace(/\D/g, '');
    if (phoneDigits.length < 8) {
      setErrorMsg('Please enter a valid active phone number.');
      return;
    }

    if (normalizedWhatsappNumber && normalizedWhatsappNumber.replace(/\D/g, '').length < 8) {
      setErrorMsg('WhatsApp number looks too short. Please correct it or leave it empty.');
      return;
    }

    if (joinCode.trim().toUpperCase() !== defaultJoinCode.toUpperCase()) {
      setErrorMsg(`Invalid Group Join Code. The correct join code for this turf group is ${defaultJoinCode}`);
      return;
    }

    if (password.length < 8) {
      setErrorMsg('Please choose a password with at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);

    const userId = `usr_${Date.now()}`;
    const memberId = `mem_${Date.now()}`;

    const newUser: UserAccount = {
      id: userId,
      email: email || `${displayName.toLowerCase().replace(/\s+/g, '')}@gigaballsfc.com`,
      fullName,
      displayName,
      nickname: displayName.split(' ')[0],
      avatar: avatarUrl,
      bio: `Turf player for GigaBalls FC. ${primaryPosition} maestro.`,
      favoriteClub,
      phoneNumber: normalizedPhoneNumber,
      whatsappNumber: normalizedWhatsappNumber || undefined,
      favoritePlayer: 'Kevin De Bruyne',
      favoriteNationalTeam: 'Argentina',
      coverBannerUrl: 'https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=1200&auto=format&fit=crop&q=80',
      themePreference: 'dark',
      reducedAnimations: false,
      compactMode: false,
      createdAt: new Date().toISOString(),
    };

    const newMember: GroupMemberProfile = {
      id: memberId,
      userId,
      groupId,
      role: 'player',
      displayName,
      phoneNumber: normalizedPhoneNumber,
      whatsappNumber: normalizedWhatsappNumber || undefined,
      nickname: displayName.split(' ')[0],
      jerseyNumber,
      primaryPosition,
      secondaryPositions: ['CAM', 'RW'],
      preferredFoot,
      unwantedPositions: ['GK'],
      goalkeeperAbility,
      preferredTeamColor: 'Dark',
      competitivePreference: 'Competitive',
      isActive: true,
      walletBalance: 0,
      stats: {
        matchesPlayed: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        goals: 0,
        assists: 0,
        cleanSheets: 0,
        motmCount: 0,
        attendancePct: 100,
        winPct: 0,
        averageRating: 7.5,
        lateArrivals: 0,
        noShows: 0,
      },
      footballCard: {
        overall: 78,
        pace: 76,
        shooting: 74,
        passing: 78,
        dribbling: 77,
        defending: 68,
        physical: 72,
      },
    };

    const registerError = await onRegisterMember(newMember, newUser, password);
    if (registerError) {
      setErrorMsg(registerError);
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
    setPassword('');
    setConfirmPassword('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Modal Header */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-emerald-500/15 border border-emerald-500/30 rounded-xl text-emerald-400">
              <UserPlus className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-lg text-white">Join GigaBalls FC as New Player</h3>
              <p className="text-[11px] text-slate-400">Register your group-specific player profile</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4 flex-1 text-xs">
          {errorMsg && (
            <div className="p-3 bg-red-500/20 border border-red-500/40 text-red-300 rounded-xl font-bold">
              {errorMsg}
            </div>
          )}

          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
            <h4 className="font-extrabold text-xs uppercase text-emerald-400 flex items-center gap-1.5">
              <Shield className="w-4 h-4" /> Group Verification Code
            </h4>
            <div>
              <label className="block text-slate-300 font-bold mb-1">Group Join Code</label>
              <input
                type="text"
                required
                value={joinCode}
                onChange={(e) => {
                  setJoinCode(e.target.value);
                  setErrorMsg(null);
                }}
                placeholder="e.g. BLK2026"
                className="w-full bg-slate-900 border border-slate-700 text-amber-300 font-mono font-black rounded-xl p-2.5 uppercase tracking-wider text-sm focus:outline-none focus:border-emerald-500"
              />
              <p className="text-[10px] text-slate-400 mt-1">Default group join code is <strong>{defaultJoinCode}</strong></p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-extrabold text-xs uppercase text-slate-300">Player Identity & Photo</h4>
            
            {/* Photo Selection */}
            <div className="flex items-center gap-3 bg-slate-950 p-3 rounded-2xl border border-slate-800">
              <img src={avatarUrl} alt="Avatar Preview" className="w-12 h-12 rounded-xl object-cover border border-emerald-500/40" />
              <div className="flex-1 space-y-1">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 font-bold border border-emerald-500/40 rounded-lg transition text-[11px] flex items-center gap-1.5"
                >
                  <Upload className="w-3.5 h-3.5" /> Select Profile Photo File
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                <p className="text-[9px] text-slate-400">Upload custom image from phone camera or gallery</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Tariq Hasan"
                  className="w-full bg-slate-950 border border-slate-800 text-white rounded-xl p-2.5 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Display Name (Kits)</label>
                <input
                  type="text"
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Tariq H."
                  className="w-full bg-slate-950 border border-slate-800 text-white rounded-xl p-2.5 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="player@example.com"
                className="w-full bg-slate-950 border border-slate-800 text-slate-200 rounded-xl p-2.5 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Active Phone Number <span className="text-red-400">*</span></label>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="e.g. +8801XXXXXXXXX"
                  className="w-full bg-slate-950 border border-slate-800 text-white rounded-xl p-2.5 focus:outline-none"
                />
                <p className="text-[10px] text-slate-400 mt-1">Required so organizer/admin can contact this player.</p>
              </div>
              <div>
                <label className="block text-slate-300 font-bold mb-1">WhatsApp Number <span className="text-slate-500">(Optional)</span></label>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  placeholder="Same or different WhatsApp number"
                  className="w-full bg-slate-950 border border-slate-800 text-slate-200 rounded-xl p-2.5 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="w-full bg-slate-950 border border-slate-800 text-slate-200 rounded-xl p-2.5 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-slate-300 font-bold mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  className="w-full bg-slate-950 border border-slate-800 text-slate-200 rounded-xl p-2.5 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <h4 className="font-extrabold text-xs uppercase text-emerald-400">Group Football Settings</h4>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Preferred Jersey Number</label>
                <div className="relative">
                  <Hash className="w-4 h-4 text-slate-500 absolute left-3 top-3" />
                  <NumberInput
                    value={jerseyNumber}
                    onCommit={setJerseyNumber}
                    min={1}
                    max={99}
                    emptyDefault={10}
                    className="w-full bg-slate-950 border border-slate-800 text-amber-300 font-mono font-bold rounded-xl pl-9 p-2.5 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Primary Pitch Position</label>
                <select
                  value={primaryPosition}
                  onChange={(e) => setPrimaryPosition(e.target.value as FootballPosition)}
                  className="w-full bg-slate-950 border border-slate-800 text-white font-mono font-bold rounded-xl p-2.5 focus:outline-none"
                >
                  {['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'LM', 'RM', 'LW', 'RW', 'ST'].map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Preferred Foot</label>
                <select
                  value={preferredFoot}
                  onChange={(e) => setPreferredFoot(e.target.value as PreferredFoot)}
                  className="w-full bg-slate-950 border border-slate-800 text-slate-200 rounded-xl p-2.5"
                >
                  <option value="Right">Right Foot</option>
                  <option value="Left">Left Foot</option>
                  <option value="Both">Both Feet (Ambidextrous)</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Can Play Goalkeeper?</label>
                <select
                  value={goalkeeperAbility}
                  onChange={(e) => setGoalkeeperAbility(e.target.value as GoalkeeperAbility)}
                  className="w-full bg-slate-950 border border-slate-800 text-slate-200 rounded-xl p-2.5"
                >
                  <option value="Yes">Yes (Willing GK)</option>
                  <option value="Sometimes">Sometimes / If Needed</option>
                  <option value="No">No (Outfield Only)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-3 rounded-xl bg-slate-800 text-slate-300 font-bold hover:bg-slate-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-950 font-black transition shadow-lg shadow-emerald-500/20 flex items-center gap-1.5"
            >
              <Check className="w-4 h-4 stroke-[3]" /> {isSubmitting ? 'Creating account…' : 'Register & Join Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
